import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AboutCompany from "../Components/productListcomponents/companyContent";
import Cartproduct from "../Components/cartcomponents/Cartproduct";
import { showErrorToast, showSuccessToast } from "../utils/toaster";
import { baseurl } from "../config/url";

const Cart = () => {
  const [carts, setCarts] = useState([]);
  const [popupMessage, setPopupMessage] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${baseurl}/api/cart/get-cart/${userId}`);
      setCarts([response.data]); // Ensure it's an array
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [userId]);

  const handleCheckout = (cartId) => {
    const cart = carts.find((cart) => cart._id === cartId);
    const sellerProduct = cart.items.find(
      (item) => item.productId.saleType === "Sale By Seller"
    );

    if (sellerProduct) {
      setPopupMessage("This product is sold directly by the seller. Would you like to contact the seller?");
      setSelectedSeller(sellerProduct.productId.seller);
    } else {
      navigate("/checkout", { cartIs: cartId });
    }
  };

  const handlePopupAction = async (action) => {
    if (action === "contact" && selectedSeller) {
      try {
        const response = await axios.get(`${baseurl}/api/seller/seller-by-id/${selectedSeller}`);
        const sellerDetails = response.data;

        if (sellerDetails.address) {
          showErrorToast(
            `Seller Contact Details:\nName: ${sellerDetails.name}\nCompany: ${sellerDetails.companyName}\nEmail: ${sellerDetails.email}\nMobile: ${sellerDetails.mobile}\nAddress: ${sellerDetails.address.street}, ${sellerDetails.address.city}, ${sellerDetails.address.state}, ${sellerDetails.address.country}, ${sellerDetails.address.postalCode}`
          );
        } else {
          showErrorToast("Address information is missing.");
        }
      } catch (error) {
        console.error("Error fetching seller details:", error);
      }
    }

    setPopupMessage(null);
    setSelectedSeller(null);
  };

  const handleRemoveFromCart = async (cartId, itemId) => {
    try {
      const response = await axios.post(`${baseurl}/api/cart/remove-item`, {
        cartId,
        itemId,
      });

      setCarts((prevCarts) =>
        prevCarts.map((cart) =>
          cart._id === cartId ? { ...cart, items: response.data.cart.items } : cart
        )
      );
      showSuccessToast("Item removed from cart");
      fetchCart();
    } catch (error) {
      console.error("Error removing item from cart:", error);
    }
  };

  const handleQuantityChange = async (cartId, itemId, change, productId) => {
    let data = {
      userId: userId,
      items: [
        {
          productId: productId,
          quantity: change,
        },
      ],
    };
    try {
      await axios.post(`${baseurl}/api/cart/add-to-cart`, data);
      fetchCart();
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        "An error occurred while adding to the cart. Please try again.";
      showErrorToast(errorMessage);
    }
  };

  const calculateTotal = () => {
    const totalAmount = carts.reduce((total, cart) => {
      return (
        total +
        cart.items.reduce((subTotal, item) => {
          if (item.productId && item.productId.price) {
            return subTotal + item.quantity * item.productId.price;
          }
          return subTotal;
        }, 0)
      );
    }, 0);

    const taxRate = 0.18;
    const taxes = totalAmount * taxRate;
    return {
      totalAmount,
      taxes,
      grandTotal: totalAmount + taxes,
    };
  };

  if (!carts.length) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <span className="text-2xl font-semibold text-gray-800 mb-4">
            Your cart is empty.
          </span>
          <button
            onClick={() => navigate("/Buyerschoice")}
            className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const { totalAmount, taxes, grandTotal } = calculateTotal();

  return (
    <div className="container mx-auto py-8 flex flex-col lg:flex-row gap-8">
      <div className="flex-1">
        {carts.map((cart) => (
          <Cartproduct
            key={cart._id}
            cart={cart}
            handleQuantityChange={handleQuantityChange}
            handleRemoveFromCart={handleRemoveFromCart}
          />
        ))}
        <AboutCompany />
      </div>

      <div className="w-full lg:w-1/3 px-6 pb-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-lg text-white min-h-fit max-h-fit">
        <h2 className="text-2xl font-semibold mb-4 text-center pb-3">
          Billing Details
        </h2>
        <div className="space-y-6">
          {carts[0]?.items?.map((item) => {
            const productPrice = item.productId.price * item.quantity;
            const productGstRate = item.productId.gstNumber / 100;
            const productTax = productPrice * productGstRate;

            return (
              <div key={item.productId._id} className="space-y-2">
                <div className="flex justify-between items-center">
                  <p className="text-lg">{item.productId.name} Total:</p>
                  <p className="text-2xl font-semibold">₹{productPrice.toFixed(2)}</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-base">GST ({item.productId.gstNumber}%):</p>
                  <p className="text-base font-semibold">₹{productTax.toFixed(2)}</p>
                </div>
              </div>
            );
          })}

          <hr className="my-4 border-gray-300" />

          <div className="flex justify-between items-center">
            <p className="text-lg font-semibold">Grand Total:</p>
            <p className="text-2xl font-bold">
              ₹
              {carts[0]?.items
                .reduce((total, item) => {
                  const productPrice = item.productId.price * item.quantity;
                  const productGstRate = item.productId.gstNumber / 100;
                  const productTax = productPrice * productGstRate;
                  return total + productPrice + productTax;
                }, 0)
                .toFixed(2)}
            </p>
          </div>
        </div>

        <button
          onClick={() => handleCheckout(carts[0]?._id)}
          className="mt-8 w-full px-6 py-3 bg-white text-indigo-600 font-bold rounded-lg hover:bg-indigo-100 transition"
        >
          Proceed to Checkout
        </button>

        <div className="mt-4 text-sm italic text-gray-200 text-center">
          *Shipping fees are calculated at checkout.
        </div>
      </div>

      {/* 🟡 Popup for contacting seller */}
      {popupMessage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full text-center">
            <p className="text-xl font-semibold text-gray-800 mb-6">{popupMessage}</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => handlePopupAction("contact")}
                className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition"
              >
                Contact Seller
              </button>
              <button
                onClick={() => setPopupMessage(null)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🟢 Floating Go to Home Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={() => navigate("/")}
          className="bg-green-600 text-white px-5 py-2 rounded-full shadow-md hover:bg-green-700 transition"
        >
          Go to Home
        </button>
      </div>
    </div>
  );
};

export default Cart;
